import type {
  ProjectService,
  ProjectSubservice,
} from "../../ProjectServicesV2/types";
import type { Governance } from "../../GovernanceV2/types/Governance";
import { lineKey } from "../builder/scopeLines";
import type { LineType } from "../builder/types";

// The change request project's lines and governance as Show displays them.

export interface ChildLineSource {
  quantity: number;
  effort: number;
  hours: number; // the source line's total hours
}

export interface ChildLine {
  key: string;
  name: string;
  quantity: number;
  effort: number; // per-unit hours on the change request line
  customHours: boolean;
  resourceLabel: string;
  hours: number; // what this line adds (its extended hours)
  revenue: number | null; // null when pricing isn't available
  source: ChildLineSource | null; // null on a new line or a missing source
  sourceMissing: boolean;
}

export interface ChildService {
  line: ChildLine;
  subservices: ChildLine[];
}

export interface ChildGovernance {
  id: string;
  description: string;
  calculatedBy: string;
  hours: number;
  revenue: number | null;
}

// Revenue keyed "<type>:<source id>", e.g. "project-services:10".
export type PricingIndex = Record<string, number>;

export interface PricingRecord {
  id?: string;
  attributes?: {
    "source-id"?: number | string;
    "service-revenue"?: number | string;
  };
}

// Project-pricing ids are "<type>-<source id>"; a line split across phases
// has several records, so they're summed.
export const pricingIndex = (
  records: PricingRecord[] | undefined
): PricingIndex | null => {
  if (!records) return null;
  return records.reduce<PricingIndex>((index, record) => {
    const type = (record.id ?? "").replace(/-\d+$/, "");
    const sourceId = record.attributes?.["source-id"];
    if (!type || sourceId === undefined || sourceId === null) return index;
    const key = `${type}:${sourceId}`;
    const revenue = parseFloat(String(record.attributes?.["service-revenue"]));
    index[key] = (index[key] ?? 0) + (isNaN(revenue) ? 0 : revenue);
    return index;
  }, {});
};

const toChildLine = (
  type: LineType,
  item: ProjectService | ProjectSubservice,
  pricing: PricingIndex | null
): ChildLine => {
  const key = lineKey(type, item.id);
  return {
    key,
    name: item.name,
    quantity: Number(item.quantity ?? 1) || 0,
    effort: Number(item.hours ?? 0) || 0,
    customHours: item.variableRates === true,
    resourceLabel: item.resource?.label ?? "",
    hours: item.extendedHours ?? 0,
    revenue: pricing ? pricing[key] ?? 0 : null,
    source:
      item.sourceExtendedHours === null || item.sourceExtendedHours === undefined
        ? null
        : {
            quantity: item.sourceQuantity ?? 1,
            effort: item.sourceHours ?? 0,
            hours: item.sourceExtendedHours,
          },
    sourceMissing: item.sourceMissing === true,
  };
};

export const toChildScopeServices = (
  services: ProjectService[],
  pricing: PricingIndex | null
): ChildService[] =>
  services.map((service) => ({
    line: toChildLine("project-services", service, pricing),
    subservices: (service.subservices ?? []).map((sub) =>
      toChildLine("project-subservices", sub, pricing)
    ),
  }));

export const toChildGovernances = (
  governances: Governance[],
  pricing: PricingIndex | null
): ChildGovernance[] =>
  governances.map((g) => ({
    id: String(g.id),
    description: g.description,
    calculatedBy: String(g.calculationTypeForDisplay ?? ""),
    hours: Number(g.hours) || 0,
    revenue: pricing ? pricing[`project-governances:${g.id}`] ?? 0 : null,
  }));

export interface GroupHours {
  original: number | null; // null when no line in the group was copied
  added: number;
}

// A service row rolls up its subservices, as on New. Original sums only the
// lines copied from the parent.
export const groupHours = (lines: ChildLine[]): GroupHours => {
  const copied = lines.filter((l) => l.source !== null);
  return {
    original: copied.length
      ? copied.reduce((sum, l) => sum + (l.source as ChildLineSource).hours, 0)
      : null,
    added: lines.reduce((sum, l) => sum + l.hours, 0),
  };
};

export const groupRevenue = (lines: ChildLine[]): number | null =>
  lines.some((l) => l.revenue === null)
    ? null
    : lines.reduce((sum, l) => sum + (l.revenue as number), 0);
